import { parsePositionIds } from "services/positions.service";
import type { AmendmentChange, EmploymentTermsValues } from "services/contract-amendment.service";

/**
 * The position / salary / hours part of an amendment form. A part is
 * `null` while the user has not added it; once added, its fields hold what
 * is being typed (numbers stay strings until the payload is built).
 */
export interface EmploymentTermsDraft {
  positions: {
    /** Ordered catalogue ids — the backend derives the LT/EN names from them. */
    ids: string[];
    /** Pre-catalogue amendment that carried only free text: shown as a hint to re-pick. */
    legacyText: string;
  } | null;
  salary: string | null;
  hoursPerWeek: string | null;
}

/** What applies to the employee today — prefills the salary / hours fields. */
export interface CurrentTerms {
  positionLT?: string;
  positionEN?: string;
  salary?: string | number;
  hoursPerWeek?: string | number;
  /** The second salary already scheduled (docs/prd/probation-salary-review.md §4/§7.3) — null once it has no effect (past, or not set). */
  plannedSalaryChange?: { date: string | number; amount: string | number } | null;
}

export const EMPTY_TERMS_DRAFT: EmploymentTermsDraft = { positions: null, salary: null, hoursPerWeek: null };

export const EMPTY_CHANGE_ROW: AmendmentChange = { point: "", text: "", action: "change" };

export function hasAnyTerms(draft: EmploymentTermsDraft): boolean {
  return draft.positions !== null || draft.salary !== null || draft.hoursPerWeek !== null;
}

/** The i18n key of the first thing missing from the added parts, or null when all are filled. */
export function termsErrorKey(draft: EmploymentTermsDraft): string | null {
  if (draft.positions && draft.positions.ids.length === 0) return "positions_required";
  if (draft.salary === "") return "salary_update_required";
  if (draft.hoursPerWeek === "") return "hours_update_required";
  return null;
}

/**
 * Body of the set-terms request: just the three values (that endpoint always
 * means «these are employment terms»). Parts that were not added are null,
 * i.e. left untouched.
 */
export function buildSetTermsPayload(draft: EmploymentTermsDraft): EmploymentTermsValues {
  return {
    newPositionIds: draft.positions ? draft.positions.ids : null,
    newSalary: draft.salary ? Number(draft.salary) : null,
    newHoursPerWeek: draft.hoursPerWeek ? Number(draft.hoursPerWeek) : null,
  };
}

/** The same three values plus the `isEmploymentTermsChange` flag — for create, edit and register. */
export function buildEmploymentTermsPayload(draft: EmploymentTermsDraft) {
  return { isEmploymentTermsChange: hasAnyTerms(draft), ...buildSetTermsPayload(draft) };
}

/** The fields of a saved amendment (as the list returns it) that the terms draft starts from. */
export interface SavedAmendmentTerms {
  isEmploymentTermsChange?: boolean;
  /** JSON array string or array — parse with parsePositionIds. */
  newPositionIds?: string[] | string | null;
  newPositionLT?: string | null;
  newPositionEN?: string | null;
  newSalary?: number | null;
  newHoursPerWeek?: number | null;
}

/** Which of the three term groups a saved amendment changes — what its chips name. */
export function changedTermGroups(amendment: SavedAmendmentTerms) {
  return {
    positions:
      parsePositionIds(amendment.newPositionIds).length > 0 || !!amendment.newPositionLT || !!amendment.newPositionEN,
    salary: amendment.newSalary != null,
    hours: amendment.newHoursPerWeek != null,
  };
}

/** Whether the amendment changes at least one of position, salary and working hours. */
export function changesAnyTermGroup(amendment: SavedAmendmentTerms): boolean {
  return Object.values(changedTermGroups(amendment)).some(Boolean);
}

/** Prefills an edit form: the parts a saved employment-terms amendment already carries. */
export function draftFromAmendment(amendment: SavedAmendmentTerms): EmploymentTermsDraft {
  if (!amendment.isEmploymentTermsChange) return EMPTY_TERMS_DRAFT;
  const changed = changedTermGroups(amendment);
  const positionIds = parsePositionIds(amendment.newPositionIds);
  return {
    positions: changed.positions
      ? { ids: positionIds, legacyText: positionIds.length === 0 ? amendment.newPositionLT || "" : "" }
      : null,
    salary: changed.salary ? String(amendment.newSalary) : null,
    hoursPerWeek: changed.hours ? String(amendment.newHoursPerWeek) : null,
  };
}

/** The change rows of a saved amendment for an edit form; one empty row when it has none. */
export function changeRowsFromAmendment(amendment: {
  changes?: { point?: string; text?: string; action?: string; isNew?: boolean }[];
}): AmendmentChange[] {
  if (!amendment.changes || amendment.changes.length === 0) return [EMPTY_CHANGE_ROW];
  return amendment.changes.map((change) => ({
    point: change.point || "",
    text: change.text || "",
    action: change.action || (change.isNew ? "new" : "change"),
  }));
}

export function withoutEmptyChanges(changes: AmendmentChange[]): AmendmentChange[] {
  return changes.filter((change) => change.point.trim() || change.text.trim());
}

/**
 * Free text alone never updates «Sutarties informacija» — only the
 * position / salary / hours parts do. So once someone has written a change
 * but added none of those, we point them to the buttons.
 */
export function shouldHintTerms(changes: AmendmentChange[], draft: EmploymentTermsDraft): boolean {
  return !hasAnyTerms(draft) && changes.some((change) => change.text.trim() !== "");
}
